import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Printer, CheckCircle, XCircle, CreditCard, Package, User, Calendar } from 'lucide-react';
import vendaService, { VendaResponseDTO, VendaPagamentoDTO, VendaItemDTO } from '../services/vendaService';
import alertService from '../services/alertService';
import paymentService from '../services/paymentService';
import { PaymentResponseDTO } from '../types/payment';

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDateTime = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '-' : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(d);
};

const STATUS_BADGE: Record<string, string> = {
  PENDENTE: 'bg-yellow-100 text-yellow-700',
  FINALIZADA: 'bg-green-100 text-green-700',
  CANCELADA: 'bg-red-100 text-red-700',
  EM_PROCESSAMENTO: 'bg-blue-100 text-blue-700',
  ENTREGUE: 'bg-emerald-100 text-emerald-700',
  PARCIALMENTE_ENTREGUE: 'bg-orange-100 text-orange-700',
};

const FORMA_PAGAMENTO_LABEL: Record<VendaPagamentoDTO['formaPagamento'], string> = {
  DINHEIRO: 'Dinheiro',
  CARTAO_CREDITO: 'Cartão de Crédito',
  CARTAO_DEBITO: 'Cartão de Débito',
  PIX: 'PIX',
  BOLETO: 'Boleto',
};

const VendaDetalhePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [venda, setVenda] = useState<VendaResponseDTO | null>(null);
  const [payments, setPayments] = useState<PaymentResponseDTO[]>([]);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  const carregar = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const resp = await vendaService.obter(id);
      const raw: any = resp?.data;
      const vendaData: any = raw && raw.data ? raw.data : raw;
      const normalized: any = vendaData ? {
        ...vendaData,
        criadoEm: vendaData.criadoEm ?? vendaData.dataCriacao ?? vendaData.createdAt ?? '',
        atualizadoEm: vendaData.atualizadoEm ?? vendaData.dataAtualizacao ?? vendaData.updatedAt ?? '',
        itens: Array.isArray(vendaData.itens) ? vendaData.itens : (vendaData.items ?? []),
        pagamentos: Array.isArray(vendaData.pagamentos) ? vendaData.pagamentos : (vendaData.payments ?? []),
        valorTotal: vendaData.valorTotal ?? ((Array.isArray(vendaData.itens) ? vendaData.itens : (vendaData.items ?? []))
          .reduce((s: number, it: any) => s + ((it?.valorTotal ?? ((it?.precoUnitario ?? 0) * (it?.quantidade ?? 0)))), 0)),
      } : null;
      setVenda(normalized || null);

      // Carregar pagamentos específicos da venda usando rota dedicada
      const pRes = await paymentService.listBySalePath(id);
      // A resposta pode vir como PageResponse ou como array direto; normalizar
      const pRaw: any = pRes?.data;
      const pList: PaymentResponseDTO[] = Array.isArray(pRaw) ? pRaw : (pRaw?.content ?? []);
      setPayments(pList);
    } catch (err: any) {
      alertService.error('Erro ao carregar venda', err?.message || 'Tente novamente');
      navigate('/vendas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const abrirCupom = () => {
    if (!id) return;
    window.open(`/pdv/cupom/${id}`, '_blank');
  };

  const finalizarVenda = async () => {
    if (!id) return;
    const confirm = await alertService.confirm('Finalizar venda?', 'A venda será marcada como FINALIZADA.');
    if (!confirm.isConfirmed) return;
    try {
      alertService.loading('Finalizando venda...');
      await vendaService.finalizar(id);
      alertService.close();
      alertService.success('Venda finalizada');
      carregar();
    } catch (err: any) {
      alertService.close();
      alertService.error('Erro ao finalizar venda', err?.message || 'Tente novamente');
    }
  };

  const cancelarVenda = async () => {
    if (!id) return;
    const confirm = await alertService.confirm('Cancelar venda?', 'A venda será marcada como CANCELADA.');
    if (!confirm.isConfirmed) return;
    try {
      alertService.loading('Cancelando venda...');
      await vendaService.cancelar(id);
      alertService.close();
      alertService.success('Venda cancelada');
      carregar();
    } catch (err: any) {
      alertService.close();
      alertService.error('Erro ao cancelar venda', err?.message || 'Tente novamente');
    }
  };

  const cancelarVendaFinalizadaFlow = async () => {
    if (!id) return;
    const confirm = await alertService.confirm('Cancelar venda FINALIZADA?', 'Deseja realmente cancelar esta venda?');
    if (!confirm.isConfirmed) return;
    setCancelReason('');
    setCancelModalOpen(true);
  };

  const submitCancelFinalizada = async () => {
    if (!id) return;
    const motivo = (cancelReason || '').trim();
    if (!motivo) {
      alertService.error('Informe o motivo do cancelamento');
      return;
    }
    try {
      setCancelLoading(true);
      alertService.loading('Cancelando venda...');
      await vendaService.cancelar(id, motivo);
      alertService.close();
      setCancelModalOpen(false);
      alertService.success('Venda cancelada com sucesso');
      carregar();
    } catch (err: any) {
      alertService.close();
      alertService.error('Erro ao cancelar venda', err?.message || 'Tente novamente');
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64 text-gray-600">
          <ShoppingCart className="h-6 w-6 animate-pulse" />
          <span className="ml-3">Carregando venda...</span>
        </div>
      </div>
    );
  }

  if (!venda) {
    return (
      <div>
        <div className="text-center py-12">
          <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Venda não encontrada</h3>
          <p className="mt-1 text-sm text-gray-500">A venda solicitada não foi encontrada.</p>
          <div className="mt-6">
            <button onClick={() => navigate('/vendas')} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg">
              Voltar para Vendas
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/vendas')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-3 flex-1">
          <ShoppingCart className="h-8 w-8 text-red-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Detalhe da Venda</h1>
            <p className="text-gray-600">Visualize as informações, itens e pagamentos</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={abrirCupom} className="px-4 py-2 border border-gray-300 rounded-lg flex items-center gap-2 hover:bg-gray-50">
            <Printer className="h-4 w-4" /> Cupom
          </button>
          {['PENDENTE','EM_PROCESSAMENTO'].includes(venda.status) && (
            <>
              <button onClick={finalizarVenda} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" /> Finalizar
              </button>
              <button onClick={cancelarVenda} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                <XCircle className="h-4 w-4" /> Cancelar
              </button>
            </>
          )}
          {venda.status === 'FINALIZADA' && (
            <button onClick={cancelarVendaFinalizadaFlow} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
              <XCircle className="h-4 w-4" /> Cancelar
            </button>
          )}
        </div>
      </div>

      {/* Resumo */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-gray-500" />
            <div>
              <div className="text-xs text-gray-500">Cliente</div>
              <div className="text-sm text-gray-900">{venda.clienteNome || venda.clienteId || '-'}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CreditCard className="h-5 w-5 text-gray-500" />
            <div>
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-sm text-gray-900">{formatCurrency(venda.valorTotal)}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-gray-500" />
            <div>
              <div className="text-xs text-gray-500">Criado</div>
              <div className="text-sm text-gray-900">{formatDateTime(venda.criadoEm)}</div>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_BADGE[venda.status] || 'bg-gray-100 text-gray-700'}`}>{venda.status}</span>
          <span className="text-xs text-gray-500">Atualizado: {formatDateTime(venda.atualizadoEm)}</span>
        </div>
        {venda.observacoes && (
          <div className="mt-4">
            <div className="text-xs text-gray-500">Observações</div>
            <div className="text-sm text-gray-700">{venda.observacoes}</div>
          </div>
        )}
      </div>

      {/* Itens */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
          <Package className="h-5 w-5 text-gray-600" />
          <h2 className="text-sm font-semibold text-gray-800">Itens da Venda</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantidade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preço Unitário</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(venda.itens || []).map((item: VendaItemDTO, idx: number) => (
                <tr key={`${item.produtoId}-${idx}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.produtoNome || item.produtoId}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.quantidade}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.precoUnitario)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(item.valorTotal)}</td>
                </tr>
              ))}
              {(!venda.itens || venda.itens.length === 0) && (
                <tr>
                  <td className="px-6 py-6 text-center text-gray-600" colSpan={4}>Nenhum item</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagamentos */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-gray-600" />
          <h2 className="text-sm font-semibold text-gray-800">Formas de Pagamento</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Forma</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(payments || []).map((p: PaymentResponseDTO) => (
                <tr key={p.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.tipoPagamentoNome || p.tipoPagamentoId}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(p.valor || 0)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{p.observacoes || '-'}</td>
                </tr>
              ))}
              {(!payments || payments.length === 0) && (
                <tr>
                  <td className="px-6 py-6 text-center text-gray-600" colSpan={3}>Nenhum pagamento registrado</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {cancelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setCancelModalOpen(false)}></div>
          <div role="dialog" aria-modal="true" className="relative bg-white rounded border shadow-lg w-full max-w-xl sm:max-w-2xl p-0 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Motivo do cancelamento</h3>
            </div>
            <div className="px-4 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Descreva o motivo</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Ex.: Cancelado por erro de lançamento, cliente devolveu produtos, etc."
              />
            </div>
            <div className="sticky bottom-0 bg-white border-t px-4 py-3 flex justify-end gap-3">
              <button type="button" onClick={() => setCancelModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                Voltar
              </button>
              <button
                type="button"
                onClick={submitCancelFinalizada}
                disabled={cancelLoading}
                className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Confirmar cancelamento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendaDetalhePage;
